import React, { useState, useRef, useEffect } from "react";
import { getQuickFilterDefs } from "requests/quickList";
import { QuickListFilter, QuickListFilterField, QuickListTemplateViewModel } from "interfaces/Mueller.Business.Models";

export const QuickFilterContext = React.createContext(null);

export interface QuickFilterDefinition {
  clientId: number,
  filterKey: string,
  value: any,
  operator: string,
};

export interface QuickListQuickFilterMapping {
  templateCode: string;
  quickFilterKeys: string[];
};

export interface QuickFilterProviderValue {
  initQuickFilters: () => void,
  loadTemplates: (templates: QuickListTemplateViewModel[]) => void,
  filterDefs: QuickListFilterFieldParsedOps[],
  filters: QuickFilterDefinition[],
  clearFilters: () => void,
  addFilter: () => void,
  removeFilter: (filterClientId: number) => void,
  changeFilter: (filterChangesPartial: Partial<QuickFilterDefinition>) => void,
  filtersLastAppliedOn: number,
  applyFilters: () => void,
  hasUnappliedChanges: boolean,
  getQuickFilters: () => QuickListFilter[],
  getFilterStatusForBasyQuery: (baseQueryCode: string) => QuickFilterQueryFilterStatus,
};

export interface QuickFilterQueryFilterStatus {
  appliedFilters: QuickListFilterFieldParsedOps[];
  invalidFilters: QuickListFilterFieldParsedOps[];
};

interface QuickListFilterFieldParsedOps extends Omit<QuickListFilterField, "operators"> {
  operators: string[];
};

const fixFilterDef = (def: QuickListFilterField): QuickListFilterFieldParsedOps => ({
  ...def,
  operators: def.operators ? JSON.parse(def.operators.replace(/'/g, '"')) : [],
});

const QuickFilterProvider = ({ children }) => {
  const mounted = useRef(true);
  // last time we applied changes, we track this to know when to update
  const [lastApplied, setLastApplied] = useState<number>(Date.now);
  // tracks if we have unsaved changes or not
  const [hasUnappliedChanges, setHasUnappliedChanges] = useState<boolean>(false);
  // quick filter defs from the server
  const [filterDefs, setFilterDefs] = useState<QuickListFilterFieldParsedOps[]>();
  // map that stores base query and the quick filters that apply to them
  const [filterMapping, setFilterMapping] = useState<QuickListQuickFilterMapping[]>();
  // ql template data from the server so we can link up template codes to quick filters to know what filters change what base queries
  const [qlTemplates, setQlTemplates] = useState<QuickListTemplateViewModel[]>();
  // the filters we are working on
  const [filters, setFilters] = useState<QuickFilterDefinition[]>([]);
  // copy of filters on apply, we reference these to load the quick lists
  const [appliedFilters, setAppliedFilters] = useState<QuickFilterDefinition[]>([]);

  // cant call this on mount because the provider is rendered in the root of the app and it causes issues
  const initQuickFilters = () => {
    if (filterDefs?.length) return;

    getQuickFilterDefs(defs => setFilterDefs((defs as QuickListFilterField[]).map(fixFilterDef).sort((a, b) => a.name.localeCompare(b.name))), mounted);
  };

  const loadTemplates = (templates: QuickListTemplateViewModel[]) => setQlTemplates(templates);

  useEffect(() => {
    if (filterDefs && qlTemplates) {
      if (filterMapping) console.log("recalcing filter mapping but we already have it 0_o");

      setFilterMapping(qlTemplates.map(t => ({
        templateCode: t.templateCode,
        quickFilterKeys: Array.from(new Set(t.filterFields.map(ff => ff.quickFilterKey).filter(k => k))),
      })));
    }
  }, [filterDefs, qlTemplates]);

  useEffect(() => () => { mounted.current = false; }, []);

  const clearFilters = () => {
    setFilters([]);
    setHasUnappliedChanges(true);
  };

  const addFilter = () => {
    let nextFilterDef = filterDefs.find(def =>
      !filters.some(f => f.filterKey === def.key)
    );

    if (!nextFilterDef) return;
  
    let newFilter = {
      clientId: Math.max(0, ...filters.map(f => f.clientId)) + 1,
      filterKey: nextFilterDef.key,
      operator: nextFilterDef.operators[0],
      value: null,
    } as QuickFilterDefinition;

    setFilters([...filters, newFilter]);
    setHasUnappliedChanges(true);
  };

  // pass clientId and then any of the other properties you want to update
  const changeFilter = ({ clientId, filterKey, value, operator }: Partial<QuickFilterDefinition>) => {
    let updatedFilter = filters.find(f => f.clientId === clientId);

    if (!updatedFilter) return;
    updatedFilter = { ...updatedFilter };

    if (filterKey !== undefined) {
      updatedFilter.filterKey = filterKey;
      let def = filterDefs.find(fd => fd.key === filterKey);
      if (!def.operators.some(o => updatedFilter.operator === o)) {
        updatedFilter.operator = def.operators[0];
      }
    }

    if (value !== undefined) updatedFilter.value = value;
    if (operator !== undefined) updatedFilter.operator = operator;
    
    setFilters(filters.map(f => f.clientId === clientId ? updatedFilter : f));
    setHasUnappliedChanges(true);
  };

  const removeFilter = (filterClientId: number) => {
    setFilters(filters.filter(f => f.clientId !== filterClientId));
    setHasUnappliedChanges(true);
  };

  const getQuickFilters = (): QuickListFilter[] => {
    return appliedFilters.map(f => ({
      id: `r-${f.clientId}`, // doubt we need this
      code: f.filterKey,
      combiner: "and",
      operator: f.operator,
      operand: f.value,
      term: f.filterKey,
      title: null,
    }));
  };

  const applyFilters = () => {
    setHasUnappliedChanges(false);
    setLastApplied(Date.now);
    setAppliedFilters([...filters]); // copy just in case someone introduces a state modifying bug later? rather safe than sorry
  };

  const getFilterStatusForBasyQuery = (baseQueryCode: string) => {
    let mapping = filterMapping?.find(d => d.templateCode === baseQueryCode);

    if (mapping) {
      return {
        appliedFilters: mapping.quickFilterKeys.filter(k => appliedFilters.some(f => f.filterKey === k)).map(k => filterDefs.find(d => d.key === k)),
        invalidFilters: appliedFilters.filter(f => !mapping.quickFilterKeys.some(k => k === f.filterKey)).map(qf => filterDefs.find(d => d.key === qf.filterKey)),
      };
    }

    return { appliedFilters: [], invalidFilters: [] };
  };

  // could useMemo here in the future if things slow down at all, but then you could run into some really annoying issues with stale references so idk
  const provderValue: QuickFilterProviderValue = {
    initQuickFilters,
    filterDefs,
    filters,
    clearFilters,
    addFilter,
    removeFilter,
    changeFilter,
    filtersLastAppliedOn: lastApplied,
    applyFilters,
    hasUnappliedChanges,
    getQuickFilters,
    loadTemplates,
    getFilterStatusForBasyQuery,
  };

  return (
    <QuickFilterContext.Provider value={provderValue}>
      {children}
    </QuickFilterContext.Provider>
  );
}

export default QuickFilterProvider;
